import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import s from './LoginPage.module.scss';

const FormSchema = z
  .object({
    new_password: z.string().min(8, 'At least 8 characters'),
    confirm_password: z.string().min(1, 'Required'),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });
type FormValues = z.infer<typeof FormSchema>;

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema) });

  const onSubmit = async (values: FormValues): Promise<void> => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, new_password: values.new_password }),
      });
      if (res.status === 204) {
        navigate('/', { replace: true, state: { passwordReset: true } });
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setError(
        body.error === 'invalid_or_expired_token'
          ? 'This reset link is invalid or expired. Request a new one.'
          : `Could not reset password (${res.status}).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <form
        className={s.card}
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <h1 className={s.title}>Reset password</h1>
        <p className={s.subtitle}>ultimumIT portal — choose a new password</p>

        {error && (
          <div className={s.formError} role="alert">
            {error}
          </div>
        )}

        <div className={s.field}>
          <label className={s.label} htmlFor="new_password">
            New password
          </label>
          <input
            id="new_password"
            type="password"
            autoComplete="new-password"
            className={s.input}
            aria-invalid={!!errors.new_password}
            {...register('new_password')}
          />
          {errors.new_password && <div className={s.fieldError}>{errors.new_password.message}</div>}
        </div>

        <div className={s.field}>
          <label className={s.label} htmlFor="confirm_password">
            Confirm new password
          </label>
          <input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            className={s.input}
            aria-invalid={!!errors.confirm_password}
            {...register('confirm_password')}
          />
          {errors.confirm_password && (
            <div className={s.fieldError}>{errors.confirm_password.message}</div>
          )}
        </div>

        <button type="submit" className={s.button} disabled={submitting}>
          {submitting ? 'Resetting…' : 'Reset password'}
        </button>
        <p style={{ marginTop: 12, fontSize: 13, textAlign: 'center' }}>
          <Link to="/">← Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
